import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Navbar,
  Dropdown,
  Avatar,
  Button,
  Badge,
  DarkThemeToggle,
  Flowbite,
  Tooltip,
} from "flowbite-react";
import logo_2 from "../assets/logo_2.png";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationsContext";
import { Link as SmoothLink, animateScroll as scroll } from "react-scroll";
import LoginModal from "../modals/LoginModal";
import AnimatedTokenDisplay from "./AnimatedTokenDisplay";
import {
  HiOutlineUserCircle,
  HiOutlineLogout,
  HiOutlineBell,
  HiCheck,
  HiExclamation,
  HiCreditCard,
  HiOutlineTrash,
  HiOutlineCheckCircle
} from "react-icons/hi";
import { CSSTransition } from 'react-transition-group';

function NavigationLaptop({ stay, userInfo, logout, linkDashboardTo }) {
  const navigate = useNavigate();
  const { notifications, markAllAsRead, markAsRead, deleteNotification } = useNotifications();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [bellShake, setBellShake] = useState(false);
  const dropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);

  const handleLogout = async () => {
    try {
      setDropdownOpen(false);
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef, notificationDropdownRef]);

  const unreadCount = notifications.filter(notification => !notification.read).length;

  // Add bell shake effect when unread notifications exist
  useEffect(() => {
    if (unreadCount > 0) {
      setBellShake(true);
      const timer = setTimeout(() => {
        setBellShake(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [unreadCount, notifications]);

  return (
    <Navbar
      fluid={true}
      className={`border bg-white ${stay
        ? "fixed bg-opacity-90 dark:bg-opacity-90 backdrop-blur-md dark:backdrop-blur-md shadow-xl w-[calc(100%-2rem)] p-4 -translate-x-1/2 lg:max-w-7xl left-1/2 top-6 rounded-[30px] transition-all duration-300"
        : "bg-opacity-90 dark:bg-opacity-90 backdrop-blur-md dark:backdrop-blur-md shadow-xl absolute-position w-[calc(100%-2rem)] p-4 -translate-x-1/2 lg:max-w-7xl left-1/2 top-6 rounded-[30px] transition-all duration-300"
        } z-10`}
    >
      <Navbar.Brand href="/" className="flex items-center">
        <img src={logo_2} className="mr-3 h-6 sm:h-9" alt="Flowbite Logo" />
        <span className="self-center whitespace-nowrap text-xl font-semibold">
          <span style={{ color: "#04c389" }}>Foodo</span>
          <span
            className="dark:text-[#d9d9d9]"
            style={{ color: "#313131" }}
          >
            scope
          </span>
          <span style={{ color: "#04c389" }}>.</span>

        </span>
      </Navbar.Brand>

      {userInfo ? (
        <div className="flex items-center gap-4 md:order-2">
          {userInfo.tokens !== undefined && (
            <AnimatedTokenDisplay value={userInfo.tokens} />
          )}

          <Flowbite>
            <DarkThemeToggle />
          </Flowbite>

          <div className="relative" ref={notificationDropdownRef}>
            <button
              className="relative p-2 text-gray-500 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700"
              onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
              aria-label="Notifications"
            >
              <HiOutlineBell className={`h-6 w-6 ${bellShake ? 'bell-shake' : ''}`} />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1.5 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full notification-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            <CSSTransition
              in={notificationDropdownOpen}
              timeout={150}
              classNames="dropdown"
              unmountOnExit
            >
              <div className="absolute right-0 mt-1 w-80 max-h-[80vh] rounded-lg shadow-xl bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 border border-gray-100 dark:border-gray-700 overflow-hidden z-50 dropdown-menu">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-xs font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>

                <div className="max-h-[calc(80vh-130px)] overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.map(notification => (
                      <div
                        key={notification.id}
                        className={`relative px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${notification.read ? 'opacity-75' : ''} notification-item cursor-pointer`}
                      >
                        <div className="flex items-start">
                          <div className="flex-shrink-0 mt-0.5">
                            {notification.type === 'warning' && (
                              <div className="p-1 rounded-full bg-yellow-100 text-yellow-600 dark:bg-yellow-800/30 dark:text-yellow-400">
                                <HiExclamation className="h-4 w-4" />
                              </div>
                            )}
                            {notification.type === 'payment' && (
                              <div className="p-1 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-800/30 dark:text-blue-400">
                                <HiCreditCard className="h-4 w-4" />
                              </div>
                            )}
                            {notification.type === 'security' && (
                              <div className="p-1 rounded-full bg-green-100 text-green-600 dark:bg-green-800/30 dark:text-green-400">
                                <HiCheck className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div
                            className="ml-3 flex-1 cursor-pointer"
                            onClick={() => !notification.read && markAsRead(notification.id)}
                          >
                            <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                              {notification.title}
                              {!notification.read && (
                                <span className="ml-2 notification-dot"></span>
                              )}
                            </p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {notification.message}
                            </p>
                            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                              {notification.timestamp}
                            </p>
                          </div>
                          <div className="absolute top-3 right-2 flex space-x-1">
                            {!notification.read && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification.id);
                                }}
                                className="text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:text-green-400 transition-colors p-1 rounded-full cursor-pointer"
                                aria-label="Mark as read"
                                title="Mark as read"
                              >
                                <HiOutlineCheckCircle className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification.id);
                              }}
                              className="text-gray-400 hover:text-gray-800 hover:bg-gray-100 dark:hover:text-gray-300 transition-colors p-1 rounded-full cursor-pointer"
                              aria-label="Delete notification"
                              title="Delete notification"
                            >
                              <HiOutlineTrash className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="notification-empty-state">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <p className="text-sm text-gray-500 dark:text-gray-400">No notifications yet</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">We'll notify you when something arrives</p>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-2.5 text-center border-t border-gray-100 dark:border-gray-700">
                  <button
                    onClick={() => {
                      navigate('/profile?tab=notifications');
                      setNotificationDropdownOpen(false);
                    }}
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-green-600 hover:bg-gray-100 dark:hover:text-green-400 transition-colors px-2 py-1 rounded-md cursor-pointer"
                  >
                    View all notifications
                  </button>
                </div>
              </div>
            </CSSTransition>
          </div>

          <div className="relative" ref={dropdownRef}>
            <Avatar
              rounded
              alt="User profile"
              img="https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="cursor-pointer"
            />
            <CSSTransition
              in={dropdownOpen}
              timeout={150}
              classNames="dropdown"
              unmountOnExit
            >
              <div className="absolute right-0 mt-2 w-56 rounded-xl shadow-xl bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 border border-gray-100 dark:border-gray-700 overflow-hidden z-50 dropdown-menu">
                <div className="py-1 bg-white dark:bg-gray-800">
                  <button
                    onClick={() => { navigate('/profile'); setDropdownOpen(false); }}
                    className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 profile-dropdown-item cursor-pointer"
                  >
                    <HiOutlineUserCircle className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2.5" />
                    <span>Profile</span>
                  </button>
                  <button
                    onClick={() => { navigate('/profile?tab=api-key'); setDropdownOpen(false); }}
                    className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 profile-dropdown-item cursor-pointer"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    <span>API Key</span>
                  </button>
                  <button
                    onClick={() => { navigate('/profile?tab=tokens'); setDropdownOpen(false); }}
                    className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 profile-dropdown-item cursor-pointer"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2.5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                    </svg>
                    <span>Tokens</span>
                  </button>
                  <button
                    onClick={() => { navigate('/profile?tab=settings'); setDropdownOpen(false); }}
                    className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 profile-dropdown-item cursor-pointer"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                    </svg>
                    <span>Settings</span>
                  </button>
                </div>
                <div className="border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <button
                    onClick={() => { handleLogout(); setDropdownOpen(false); }}
                    className="flex w-full items-center px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500 transition-colors duration-200 group cursor-pointer"
                  >
                    <HiOutlineLogout className="h-5 w-5 mr-2.5 group-hover:text-red-700 dark:group-hover:text-white" />
                    <span className="font-medium group-hover:text-red-700 dark:group-hover:text-white">Log out</span>
                  </button>
                </div>
              </div>
            </CSSTransition>
          </div>
          <Navbar.Toggle />
        </div>
      ) : (
        <div className="flex gap-1 md:gap-3 md:order-2">
          <Flowbite>
            <DarkThemeToggle className="rounded-full" />
          </Flowbite>
          <Link to="/signup">
            <Button pill="true" color="light" className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200">Signup</Button>
          </Link>
          <Link to='/login'>
            <Button pill="true" color="light" className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200">Login</Button>
          </Link>
        </div>
      )}

      {userInfo ? (
        <Navbar.Collapse>
          <Navbar.Link>
            <Link to="/" className="hover:text-green-500 transition-colors duration-200">
              Home
            </Link>
          </Navbar.Link>
          <Navbar.Link>
            <a
              href="/playground"
              rel="noopener noreferrer"
              className="hover:text-green-500 transition-colors duration-200"
            >
              Playground
            </a>
          </Navbar.Link>
          <Navbar.Link>
            <a
              href="/analytics"
              rel="noopener noreferrer"
              className="hover:text-green-500 transition-colors duration-200"
            >
              Analytics
            </a>
          </Navbar.Link>
        </Navbar.Collapse>
      ) : (
        <Navbar.Collapse>
          <Navbar.Link>
            <SmoothLink to="hero" className="hover:text-green-500 transition-colors duration-200 cursor-pointer">
              Home
            </SmoothLink>
          </Navbar.Link>
          <Navbar.Link>
            <SmoothLink
              to="about"
              offset={-87}
              smooth={true}
              className="hover:text-green-500 transition-colors duration-200 cursor-pointer"
            >
              About
            </SmoothLink>
          </Navbar.Link>
          <Navbar.Link>
            <SmoothLink
              to="pricing"
              offset={-87}
              smooth={true}
              className="hover:text-green-500 transition-colors duration-200 cursor-pointer"
            >
              Pricing
            </SmoothLink>
          </Navbar.Link>
          <Navbar.Link>
            <SmoothLink
              to="faq"
              offset={-87}
              smooth={true}
              className="hover:text-green-500 transition-colors duration-200 cursor-pointer"
            >
              FAQs
            </SmoothLink>
          </Navbar.Link>
          <Navbar.Link>
            <SmoothLink
              to="contact"
              offset={-38}
              smooth={true}
              className="hover:text-green-500 transition-colors duration-200 cursor-pointer"
            >
              Contact
            </SmoothLink>
          </Navbar.Link>
        </Navbar.Collapse>
      )}
    </Navbar>
  );
}

function NavigationMobile({ stay, userInfo, logout, linkDashboardTo }) {
  const navigate = useNavigate();
  const { notifications, markAllAsRead, markAsRead, deleteNotification } = useNotifications();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [bellShake, setBellShake] = useState(false);
  const dropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);

  const handleLogout = async () => {
    try {
      setDropdownOpen(false);
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef, notificationDropdownRef]);

  const unreadCount = notifications.filter(notification => !notification.read).length;

  // Add bell shake effect when unread notifications exist
  useEffect(() => {
    if (unreadCount > 0) {
      setBellShake(true);
      const timer = setTimeout(() => {
        setBellShake(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [unreadCount, notifications]);

  return (
    <Navbar
      fluid={true}
      className={`border bg-white ${stay
        ? "fixed bg-opacity-90 dark:bg-opacity-90 backdrop-blur-md dark:backdrop-blur-md shadow-xl w-[calc(100%-2rem)] p-4 -translate-x-1/2 lg:max-w-7xl left-1/2 top-6 rounded-[30px] transition-all duration-300"
        : "bg-opacity-90 dark:bg-opacity-90 backdrop-blur-md dark:backdrop-blur-md shadow-xl absolute-position w-[calc(100%-2rem)] p-4 -translate-x-1/2 lg:max-w-7xl left-1/2 top-6 rounded-[30px] transition-all duration-300"
        } z-10`}
    >
      <Navbar.Brand href="/" className="">
        <img src={logo_2} className="mr-3 h-6 sm:h-9" alt="Flowbite Logo" />
        <span className="self-center whitespace-nowrap text-xl font-semibold">
          <span style={{ color: "#04c389" }}>Foodo</span>
          <span
            className="dark:text-[#d9d9d9]"
            style={{ color: "#313131" }}
          >
            scope
          </span>
          <span style={{ color: "#04c389" }}>.</span>
        </span>
      </Navbar.Brand>

      {userInfo ? (
        <div className="flex items-center gap-2 md:order-2">
          {userInfo.tokens !== undefined && (
            <>
              <div className="md:hidden">
                <AnimatedTokenDisplay value={userInfo.tokens} />
              </div>
              <div className="hidden md:block">
                <AnimatedTokenDisplay value={userInfo.tokens} />
              </div>
            </>
          )}

          {("isAdmin" in userInfo || userInfo.isModerator) && (
            <Badge
              color={(() => {
                if ("isAdmin" in userInfo) return "purple";
                if (userInfo.isModerator) return "pink";
              })()}
              size="sm"
            >
              {(() => {
                if ("isAdmin" in userInfo) return "Admin";
                if (userInfo.isModerator) return "Moderator";
              })()}
            </Badge>
          )}

          <div className="relative" ref={notificationDropdownRef}>
            <button
              className="relative p-2 text-gray-500 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700"
              onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
              aria-label="Notifications"
            >
              <HiOutlineBell className={`h-6 w-6 ${bellShake ? 'bell-shake' : ''}`} />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1.5 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full notification-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            <CSSTransition
              in={notificationDropdownOpen}
              timeout={150}
              classNames="dropdown"
              unmountOnExit
            >
              <div className="absolute right-0 mt-1 w-72 max-w-full max-h-[80vh] rounded-lg shadow-xl bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 border border-gray-100 dark:border-gray-700 overflow-hidden z-50 dropdown-menu">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-xs font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>

                <div className="max-h-[calc(80vh-130px)] overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.map(notification => (
                      <div
                        key={notification.id}
                        className={`relative px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${notification.read ? 'opacity-75' : ''} notification-item cursor-pointer`}
                      >
                        <div className="flex items-start">
                          <div className="flex-shrink-0 mt-0.5">
                            {notification.type === 'warning' && (
                              <div className="p-1 rounded-full bg-yellow-100 text-yellow-600 dark:bg-yellow-800/30 dark:text-yellow-400">
                                <HiExclamation className="h-4 w-4" />
                              </div>
                            )}
                            {notification.type === 'payment' && (
                              <div className="p-1 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-800/30 dark:text-blue-400">
                                <HiCreditCard className="h-4 w-4" />
                              </div>
                            )}
                            {notification.type === 'security' && (
                              <div className="p-1 rounded-full bg-green-100 text-green-600 dark:bg-green-800/30 dark:text-green-400">
                                <HiCheck className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div
                            className="ml-3 flex-1 cursor-pointer"
                            onClick={() => !notification.read && markAsRead(notification.id)}
                          >
                            <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                              {notification.title}
                              {!notification.read && (
                                <span className="ml-2 notification-dot"></span>
                              )}
                            </p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {notification.message}
                            </p>
                            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                              {notification.timestamp}
                            </p>
                          </div>
                          <div className="absolute top-3 right-2 flex space-x-1">
                            {!notification.read && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification.id);
                                }}
                                className="text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:text-green-400 transition-colors p-1 rounded-full cursor-pointer"
                                aria-label="Mark as read"
                                title="Mark as read"
                              >
                                <HiOutlineCheckCircle className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification.id);
                              }}
                              className="text-gray-400 hover:text-gray-800 hover:bg-gray-100 dark:hover:text-gray-300 transition-colors p-1 rounded-full cursor-pointer"
                              aria-label="Delete notification"
                              title="Delete notification"
                            >
                              <HiOutlineTrash className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="notification-empty-state">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <p className="text-sm text-gray-500 dark:text-gray-400">No notifications yet</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">We'll notify you when something arrives</p>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-2.5 text-center border-t border-gray-100 dark:border-gray-700">
                  <button
                    onClick={() => {
                      navigate('/profile?tab=notifications');
                      setNotificationDropdownOpen(false);
                    }}
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-green-600 hover:bg-gray-100 dark:hover:text-green-400 transition-colors px-2 py-1 rounded-md cursor-pointer"
                  >
                    View all notifications
                  </button>
                </div>
              </div>
            </CSSTransition>
          </div>

          <div className="relative" ref={dropdownRef}>
            <Avatar
              rounded
              alt="User profile"
              img="https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="cursor-pointer"
            />
            <CSSTransition
              in={dropdownOpen}
              timeout={150}
              classNames="dropdown"
              unmountOnExit
            >
              <div className="absolute right-0 mt-2 w-56 rounded-xl shadow-xl bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 border border-gray-100 dark:border-gray-700 overflow-hidden z-50 dropdown-menu">
                <div className="py-1 bg-white dark:bg-gray-800">
                  <button
                    onClick={() => { navigate('/profile'); setDropdownOpen(false); }}
                    className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 profile-dropdown-item cursor-pointer"
                  >
                    <HiOutlineUserCircle className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2.5" />
                    <span>Profile</span>
                  </button>
                  <button
                    onClick={() => { navigate('/profile?tab=api-key'); setDropdownOpen(false); }}
                    className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 profile-dropdown-item cursor-pointer"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    <span>API Key</span>
                  </button>
                  <button
                    onClick={() => { navigate('/profile?tab=tokens'); setDropdownOpen(false); }}
                    className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 profile-dropdown-item cursor-pointer"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2.5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                    </svg>
                    <span>Tokens</span>
                  </button>
                  <button
                    onClick={() => { navigate('/profile?tab=settings'); setDropdownOpen(false); }}
                    className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 profile-dropdown-item cursor-pointer"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                    </svg>
                    <span>Settings</span>
                  </button>
                </div>
                <div className="border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <button
                    onClick={() => { handleLogout(); setDropdownOpen(false); }}
                    className="flex w-full items-center px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500 transition-colors duration-200 group cursor-pointer"
                  >
                    <HiOutlineLogout className="h-5 w-5 mr-2.5 group-hover:text-red-700 dark:group-hover:text-white" />
                    <span className="font-medium group-hover:text-red-700 dark:group-hover:text-white">Log out</span>
                  </button>
                </div>
              </div>
            </CSSTransition>
          </div>
          <Navbar.Toggle className="cursor-pointer" />
        </div>
      ) : (
        <div>
          <Navbar.Toggle />
        </div>
      )}

      {userInfo ? (
        <Navbar.Collapse>
          <Navbar.Link>
            <Link to="/" className="hover:text-green-500 transition-colors duration-200">
              Home
            </Link>
          </Navbar.Link>
          <Navbar.Link>
            <a
              href="/playground"
              rel="noopener noreferrer"
              className="hover:text-green-500 transition-colors duration-200"
            >
              Playground
            </a>
          </Navbar.Link>
          <Navbar.Link>
            <a
              href="/analytics"
              rel="noopener noreferrer"
              className="hover:text-green-500 transition-colors duration-200"
            >
              Analytics
            </a>
          </Navbar.Link>
          <Flowbite>
            <DarkThemeToggle />
          </Flowbite>
        </Navbar.Collapse>
      ) : (
        <Navbar.Collapse>
          <Navbar.Link>
            <SmoothLink to="hero" className="hover:text-green-500 transition-colors duration-200 cursor-pointer">
              Home
            </SmoothLink>
          </Navbar.Link>
          <Navbar.Link>
            <SmoothLink
              to="about"
              smooth={true}
              className="hover:text-green-500 transition-colors duration-200 cursor-pointer"
            >
              About
            </SmoothLink>
          </Navbar.Link>
          <Navbar.Link>
            <SmoothLink
              to="pricing"
              smooth={true}
              className="hover:text-green-500 transition-colors duration-200 cursor-pointer"
            >
              Pricing
            </SmoothLink>
          </Navbar.Link>
          <Navbar.Link>
            <SmoothLink to="faq" smooth={true} className="hover:text-green-500 transition-colors duration-200 cursor-pointer">
              FAQs
            </SmoothLink>
          </Navbar.Link>
          <Navbar.Link>
            <SmoothLink
              to="contact"
              smooth={true}
              className="hover:text-green-500 transition-colors duration-200 cursor-pointer"
            >
              Contact
            </SmoothLink>
          </Navbar.Link>

          <div className="flex justify-between mt-4">
            <Flowbite>
              <DarkThemeToggle className="rounded-full" />
            </Flowbite>
            <div className="flex flex-row gap-2">
              <Link to='/login'>
                <Button pill="true" className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200">Login</Button>
              </Link>
              <Link to="/signup">
                <Button pill="true" className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200">Signup</Button>
              </Link>
            </div>
          </div>
        </Navbar.Collapse>
      )}
    </Navbar>
  );
}

export default function Navigation({ stay }) {
  const { getProfile, getToken, logout, TOKEN_UPDATE_EVENT } = useAuth();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(() => getToken() !== 0);
  const [userInfo, setUserInfo] = useState(() => getProfile());
  const [tokens, setTokens] = useState(() => getToken());
  const [openModal, setOpenModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    // Listen for token updates, including logout
    const handleTokenUpdate = (event) => {
      const { loggedOut } = event.detail || {};
      
      if (loggedOut) {
        // Immediately update UI state on logout
        setIsUserLoggedIn(false);
        setUserInfo(null);
      } else {
        // For other token updates
        const newUserInfo = getProfile();
        setUserInfo(newUserInfo);
        setIsUserLoggedIn(getToken() !== 0);
      }
    };

    window.addEventListener(TOKEN_UPDATE_EVENT, handleTokenUpdate);
    return () => window.removeEventListener(TOKEN_UPDATE_EVENT, handleTokenUpdate);
  }, [TOKEN_UPDATE_EVENT, getProfile, getToken]);

  useEffect(() => {
    function handleScreenSize() {
      setScreenWidth(window.innerWidth);
    }

    window.addEventListener("resize", handleScreenSize);
    return () => {
      window.removeEventListener("resize", handleScreenSize);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      setUserInfo(getProfile());
      setIsUserLoggedIn(getToken() !== 0);
    } else {
      setIsUserLoggedIn(false);
    }
  }, [getProfile, getToken]);

  // Use a less frequent update interval
  useEffect(() => {
    if (userInfo) {
      const intervalId = setInterval(() => {
        setUserInfo(getProfile());
        setIsUserLoggedIn(getToken() !== 0);
      }, 30000); // Update every 30 seconds instead of 5 seconds

      return () => clearInterval(intervalId);
    }
  }, [userInfo, getProfile, getToken]);

  const linkDashboardTo = (userInfo) => {
    if (!userInfo) return '/';
    if ("isAdmin" in userInfo || userInfo.isModerator) {
      return "/admin";
    }
    return "/user";
  };

  return (
    <>
      {screenWidth > 768 ? (
        <NavigationLaptop
          stay={stay}
          userInfo={userInfo ? { ...userInfo, tokens } : null}
          logout={logout}
          linkDashboardTo={linkDashboardTo}
        />
      ) : (
        <NavigationMobile
          stay={stay}
          userInfo={userInfo ? { ...userInfo, tokens } : null}
          logout={logout}
          linkDashboardTo={linkDashboardTo}
        />
      )}
    </>
  );
}
